import { Errors } from '@livecontrol/scheduler/store';
import { useCallback, useEffect, useState } from 'react';
import { useUploadPartOfFile } from '../use-upload-part-of-file';
import { useCompleteMultipart } from './use-complete-multipart';
import { useFinishAsset } from './use-finish-asset';
import { useMultipartGenerateUrls } from './use-generate-multipart-urls';

interface Args {
    clientId: number;
    assetId: number;
    file: File;
    duration?: number;
}

interface PartInput {
    part_number: number;
    e_tag: string;
}

const VIDEO_STATUS = {
    GENERATING_LINK: 'generating link',
    UPLOADING: 'uploading',
    FINISHING_ASSET: 'finishing asset',
    NONE: '',
    ERROR: 'error'
} as const;

type VideoStatus = typeof VIDEO_STATUS[keyof typeof VIDEO_STATUS];

const FIVE_GB = 5 * 1024 * 1024 * 1024; // 5 GB en bytes
// const FIVE_GB = 7 * 1024 * 1024;  // test 7 megas

export const useReplaceAssetInChunks = (): [
    (args: Args) => Promise<void>,
    {
        loading: boolean;
        status: VideoStatus;
        progress: number;
        data?: { url: string };
        error?: Error;
        abort?: (() => void);
    }
] => {
    const [data, setData] = useState<{ url: string }>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error>();
    const [status, setStatus] = useState<VideoStatus>(VIDEO_STATUS.NONE);
    const [accumulatedProgress, setAccumulatedProgress ] = useState(0);
    const [totalParts, setTotalParts ] = useState(0);
    const [currentPart, setCurrentPart ] = useState(0);
    const [lastProgressRegistered, setLastProgressRegistered] = useState(0);

    const [generateUrls, generateResponse] = useMultipartGenerateUrls();
    const [uploadPartOfFile, uploadResponse] = useUploadPartOfFile();
    const [finishAsset, finishResponse] = useFinishAsset();
    const [completeMultipartUpload, completeUploadResponse] = useCompleteMultipart();


    useEffect(() => {
        const progressPerPart = 100 / totalParts;

        setLastProgressRegistered(uploadResponse.progress.percentage);

        if(uploadResponse.progress.percentage < lastProgressRegistered && currentPart !== (totalParts - 1)){
            setCurrentPart((currentPartCounter) => currentPartCounter + 1);
        }else{
            setAccumulatedProgress((progressPerPart * currentPart) + (uploadResponse.progress.percentage / totalParts));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentPart, totalParts, uploadResponse.progress.percentage]);


    return [
        useCallback(async ({ assetId, clientId, file, duration }: Args) => {
            setLoading(true);
            setData(undefined);
            setStatus(VIDEO_STATUS.NONE);
            setAccumulatedProgress(0);

            try {
                setStatus(VIDEO_STATUS.GENERATING_LINK);

                const fileSize = file.size;
                const numParts = Math.ceil(fileSize / FIVE_GB);

                setTotalParts(numParts);

                const { destination_url, upload_urls, upload_id } = await generateUrls({ assetId, clientId, totalParts: numParts }) ?? {};

                if (!destination_url || !upload_urls) {
                    throw Errors.serverError();
                }

                setStatus(VIDEO_STATUS.UPLOADING);

                const chunkSize = Math.ceil(fileSize / numParts);
                let start = 0;

                const eTags: PartInput[] = [];

                for (let i = 0; i < numParts; i++) {
                    const end = Math.min(start + chunkSize, fileSize);
                    const chunk = file.slice(start, end);

                    // eslint-disable-next-line no-await-in-loop
                    const eTag = await uploadPartOfFile(upload_urls[i], chunk, { headers: [['Content-Type', 'video/mp4'], ['content-disposition', 'attachment']], method: 'PUT' });


                    if(eTag){
                        eTags.push({part_number: (i + 1), e_tag: eTag});
                    }else{
                        throw Errors.serverError();
                    }

                    start = end;
                }

                await completeMultipartUpload({ assetId, clientId, uploadId: upload_id, parts: eTags });

                setStatus(VIDEO_STATUS.FINISHING_ASSET);

                await finishAsset({ assetId, clientId, archiveVideoUrl: destination_url, duration });
                setData({ url: destination_url });
                setStatus(VIDEO_STATUS.NONE);
            } catch (error_: unknown) {
                setError(<Error>error_);
                setStatus(VIDEO_STATUS.ERROR);
            }

            setLoading(false);
        }, [generateUrls, completeMultipartUpload, finishAsset, uploadPartOfFile]),
        {
            error: error ?? generateResponse.error ?? uploadResponse.error ?? finishResponse.error ?? completeUploadResponse.error,
            loading: loading || generateResponse.loading || uploadResponse.uploading || finishResponse.loading || completeUploadResponse.loading,
            progress: accumulatedProgress,
            data,
            abort: uploadResponse.abort,
            status
        }
    ];
};
